import { useState, useEffect, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { MapPin, Calendar, Users, SlidersHorizontal, X, Bed, Home } from 'lucide-react';
import { getProperties } from '@/lib/api';
import PropertyCard from '@/components/PropertyCard';
import Pagination from '@/components/Pagination';
import CurrencySwitcher from '@/components/CurrencySwitcher';
import SuperhostBadge from '@/components/SuperhostBadge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";

const PAGE_SIZE = 9;

const StaysPage = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [properties, setProperties] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showFilters, setShowFilters] = useState(false);
  const [currentPage, setCurrentPage] = useState(parseInt(searchParams.get('page') || '1', 10));
  
  const [filters, setFilters] = useState({
    location: searchParams.get('location') || '',
    checkIn: searchParams.get('check_in') || '',
    checkOut: searchParams.get('check_out') || '',
    guests: searchParams.get('guests') || '',
    bedrooms: searchParams.get('bedrooms') || '',
    propertyType: searchParams.get('property_type') || '',
    sort: searchParams.get('sort') || 'newest',
  });

  const locations = ['Canggu', 'Berawa', 'Pererenan', 'Seminyak', 'Uluwatu', 'Bingin', 'Sanur', 'Ubud'];
  const propertyTypes = [
    { value: 'villa', label: 'Villa' },
    { value: 'apartment', label: 'Apartment' },
    { value: 'townhouse', label: 'Townhouse' },
    { value: 'boutique_hotel', label: 'Boutique Hotel' },
  ];
  const bedroomOptions = [
    { value: '1', label: '1 Bedroom' },
    { value: '2', label: '2 Bedrooms' },
    { value: '3', label: '3 Bedrooms' },
    { value: '4', label: '4+ Bedrooms' },
  ];
  const guestOptions = [
    { value: '1', label: '1 Guest' },
    { value: '2', label: '2 Guests' },
    { value: '3', label: '3 Guests' },
    { value: '4', label: '4 Guests' },
    { value: '5', label: '5+ Guests' },
  ];
  const sortOptions = [
    { value: 'newest', label: 'Newest' },
    { value: 'price_asc', label: 'Price: Low to High' },
    { value: 'price_desc', label: 'Price: High to Low' },
    { value: 'bedrooms', label: 'Bedrooms' },
  ];

  useEffect(() => {
    const fetchProperties = async () => {
      setLoading(true);
      try {
        const params = {
          status: 'for_rent_short'
        };
        if (filters.location) params.location = filters.location;
        if (filters.bedrooms) params.bedrooms = filters.bedrooms;
        if (filters.propertyType) params.property_type = filters.propertyType;
        params.sort = filters.sort;

        const res = await getProperties(params);
        setProperties(res.data);
      } catch (err) {
        if (process.env.NODE_ENV !== 'production') console.error('Stays fetch failed:', err);
      } finally {
        setLoading(false);
      }
    };
    fetchProperties();
  }, [filters]);

  // Reset to page 1 when filters change
  useEffect(() => {
    setCurrentPage(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters]);

  const totalPages = Math.max(1, Math.ceil(properties.length / PAGE_SIZE));
  const safePage = Math.min(currentPage, totalPages);
  const paginatedProperties = useMemo(
    () => properties.slice((safePage - 1) * PAGE_SIZE, safePage * PAGE_SIZE),
    [properties, safePage]
  );

  const handlePageChange = (page) => {
    setCurrentPage(page);
    const params = new URLSearchParams(searchParams);
    if (page > 1) params.set('page', String(page));
    else params.delete('page');
    setSearchParams(params, { replace: true });
  };

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({ ...prev, [key]: value }));
    const params = new URLSearchParams(searchParams);
    if (value) {
      params.set(key === 'propertyType' ? 'property_type' : key === 'checkIn' ? 'check_in' : key === 'checkOut' ? 'check_out' : key, value);
    } else {
      params.delete(key === 'propertyType' ? 'property_type' : key === 'checkIn' ? 'check_in' : key === 'checkOut' ? 'check_out' : key);
    }
    params.delete('page');
    setSearchParams(params);
  };

  const clearFilters = () => {
    setFilters({
      location: '',
      checkIn: '',
      checkOut: '',
      guests: '',
      bedrooms: '',
      propertyType: '',
      sort: 'newest',
    });
    setSearchParams({});
  };

  const hasActiveFilters = filters.location || filters.checkIn || filters.checkOut || filters.guests || filters.bedrooms || filters.propertyType;

  return (
    <div className="pt-20 min-h-screen bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-8"
        >
          <div className="flex flex-wrap items-center gap-3 mb-2">
            <h1 className="font-heading text-3xl sm:text-4xl font-semibold text-charcoal">
              Browse Stays
            </h1>
            <SuperhostBadge variant="dark" />
          </div>
          <p className="font-body text-charcoal-light">
            {loading ? 'Loading...' : `${properties.length} rentals available`}
            {filters.location && ` in ${filters.location}`}
          </p>
        </motion.div>

        {/* Search Bar - Airbnb Style */}
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-warm-sand rounded-2xl p-4 mb-8"
        >
          {/* Mobile: Compact filter bar */}
          <div className="md:hidden">
            <div className="flex gap-2">
              <Select
                value={filters.location}
                onValueChange={(value) => handleFilterChange('location', value)}
              >
                <SelectTrigger className="bg-white border-gray-200 flex-1" data-testid="stays-location-mobile">
                  <MapPin className="w-4 h-4 mr-2 text-charcoal-light" />
                  <SelectValue placeholder="Location" />
                </SelectTrigger>
                <SelectContent>
                  {locations.map(loc => (
                    <SelectItem key={loc} value={loc}>{loc}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Button
                variant="outline"
                onClick={() => setShowFilters(!showFilters)}
                className="bg-white hover:bg-gray-50 border-gray-200 px-4"
                data-testid="stays-filters-toggle"
              >
                <SlidersHorizontal className="w-4 h-4" />
                {hasActiveFilters && <span className="ml-1 w-2 h-2 bg-ubud-jungle rounded-full" />}
              </Button>
            </div>
            
            {/* Mobile Expanded Filters */}
            {showFilters && (
              <motion.div
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: 'auto' }}
                className="mt-4 pt-4 border-t border-gray-200 space-y-3"
              >
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="text-xs text-charcoal-light mb-1 block">Check In</label>
                    <Input
                      type="date"
                      value={filters.checkIn}
                      onChange={(e) => handleFilterChange('checkIn', e.target.value)}
                      className="bg-white border-gray-200 h-9 text-sm"
                    />
                  </div>
                  <div>
                    <label className="text-xs text-charcoal-light mb-1 block">Check Out</label>
                    <Input
                      type="date"
                      value={filters.checkOut}
                      onChange={(e) => handleFilterChange('checkOut', e.target.value)}
                      className="bg-white border-gray-200 h-9 text-sm"
                    />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <Select
                    value={filters.guests}
                    onValueChange={(value) => handleFilterChange('guests', value)}
                  >
                    <SelectTrigger className="bg-white border-gray-200 h-9">
                      <SelectValue placeholder="Guests" />
                    </SelectTrigger>
                    <SelectContent>
                      {guestOptions.map(opt => (
                        <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <Select
                    value={filters.bedrooms}
                    onValueChange={(value) => handleFilterChange('bedrooms', value)}
                  >
                    <SelectTrigger className="bg-white border-gray-200 h-9">
                      <SelectValue placeholder="Bedrooms" />
                    </SelectTrigger>
                    <SelectContent>
                      {bedroomOptions.map(opt => (
                        <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <Select
                  value={filters.propertyType}
                  onValueChange={(value) => handleFilterChange('propertyType', value)}
                >
                  <SelectTrigger className="bg-white border-gray-200 h-9">
                    <SelectValue placeholder="Property Type" />
                  </SelectTrigger>
                  <SelectContent>
                    {propertyTypes.map(type => (
                      <SelectItem key={type.value} value={type.value}>{type.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {hasActiveFilters && (
                  <Button
                    variant="ghost"
                    onClick={clearFilters}
                    className="w-full text-charcoal-light hover:text-charcoal h-9 text-sm"
                  >
                    <X className="w-4 h-4 mr-2" />
                    Clear all filters
                  </Button>
                )}
              </motion.div>
            )}
          </div>

          {/* Desktop: Full filter bar */}
          <div className="hidden md:block">
            <div className="grid grid-cols-2 lg:grid-cols-5 gap-4">
              {/* Location */}
              <div className="space-y-1">
                <label className="flex items-center gap-2 text-sm font-medium text-charcoal-light">
                  <MapPin className="w-4 h-4" />
                  Where
                </label>
                <Select
                  value={filters.location}
                  onValueChange={(value) => handleFilterChange('location', value)}
                >
                  <SelectTrigger className="bg-white border-gray-200" data-testid="stays-location">
                    <SelectValue placeholder="Search destinations" />
                  </SelectTrigger>
                  <SelectContent>
                    {locations.map(loc => (
                      <SelectItem key={loc} value={loc}>{loc}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Check In */}
              <div className="space-y-1">
                <label className="flex items-center gap-2 text-sm font-medium text-charcoal-light">
                  <Calendar className="w-4 h-4" />
                  Check In
                </label>
                <Input
                  type="date"
                  value={filters.checkIn}
                  onChange={(e) => handleFilterChange('checkIn', e.target.value)}
                  className="bg-white border-gray-200"
                  data-testid="stays-checkin"
                />
              </div>

              {/* Check Out */}
              <div className="space-y-1">
                <label className="flex items-center gap-2 text-sm font-medium text-charcoal-light">
                  <Calendar className="w-4 h-4" />
                  Check Out
                </label>
                <Input
                  type="date"
                  value={filters.checkOut}
                  onChange={(e) => handleFilterChange('checkOut', e.target.value)}
                  className="bg-white border-gray-200"
                  data-testid="stays-checkout"
                />
              </div>

              {/* Guests */}
              <div className="space-y-1">
                <label className="flex items-center gap-2 text-sm font-medium text-charcoal-light">
                  <Users className="w-4 h-4" />
                  Guests
                </label>
                <Select
                  value={filters.guests}
                  onValueChange={(value) => handleFilterChange('guests', value)}
                >
                  <SelectTrigger className="bg-white border-gray-200" data-testid="stays-guests">
                    <SelectValue placeholder="Add guests" />
                  </SelectTrigger>
                  <SelectContent>
                    {guestOptions.map(opt => (
                      <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* More Filters Button */}
              <div className="flex items-end">
                <Button
                  variant="outline"
                  onClick={() => setShowFilters(!showFilters)}
                  className="w-full bg-white hover:bg-gray-50 border-gray-200"
                  data-testid="stays-more-filters"
                >
                  <SlidersHorizontal className="w-4 h-4 mr-2" />
                  More Filters
                </Button>
              </div>
            </div>

            {/* Expanded Filters - Desktop */}
            {showFilters && (
              <motion.div
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: 'auto' }}
                exit={{ opacity: 0, height: 0 }}
                className="mt-4 pt-4 border-t border-gray-200"
              >
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  {/* Bedrooms */}
                  <div className="space-y-1">
                    <label className="flex items-center gap-2 text-sm font-medium text-charcoal-light">
                      <Bed className="w-4 h-4" />
                      Bedrooms
                    </label>
                    <Select
                      value={filters.bedrooms}
                      onValueChange={(value) => handleFilterChange('bedrooms', value)}
                    >
                      <SelectTrigger className="bg-white border-gray-200" data-testid="stays-bedrooms">
                        <SelectValue placeholder="Any" />
                      </SelectTrigger>
                      <SelectContent>
                        {bedroomOptions.map(opt => (
                          <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  {/* Property Type */}
                  <div className="space-y-1">
                    <label className="flex items-center gap-2 text-sm font-medium text-charcoal-light">
                      <Home className="w-4 h-4" />
                      Property Type
                    </label>
                    <Select
                      value={filters.propertyType}
                      onValueChange={(value) => handleFilterChange('propertyType', value)}
                    >
                      <SelectTrigger className="bg-white border-gray-200" data-testid="stays-type">
                        <SelectValue placeholder="Any type" />
                      </SelectTrigger>
                      <SelectContent>
                        {propertyTypes.map(type => (
                          <SelectItem key={type.value} value={type.value}>{type.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  {/* Clear Filters */}
                  {hasActiveFilters && (
                    <div className="flex items-end">
                      <Button
                        variant="ghost"
                        onClick={clearFilters}
                        className="text-charcoal-light hover:text-charcoal"
                        data-testid="stays-clear-filters"
                      >
                        <X className="w-4 h-4 mr-2" />
                        Clear all filters
                      </Button>
                    </div>
                  )}
                </div>
              </motion.div>
            )}
          </div>
        </motion.div>

        {/* Sort and Results Count */}
        <div className="flex items-center justify-between mb-6 gap-3">
          <p className="text-charcoal-light text-sm">
            {!loading && `Showing ${properties.length} stays`}
          </p>
          <div className="flex items-center gap-3">
            <CurrencySwitcher variant="page" className="hidden sm:inline-flex" />
            <span className="text-sm text-charcoal-light hidden sm:inline">Sort by:</span>
            <Select
              value={filters.sort}
              onValueChange={(value) => handleFilterChange('sort', value)}
            >
              <SelectTrigger className="w-[160px] bg-white" data-testid="stays-sort">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {sortOptions.map(opt => (
                  <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>

        {/* Properties Grid */}
        {loading ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[...Array(6)].map((_, i) => (
              <div key={i} className="bg-gray-100 rounded-2xl h-80 animate-pulse" />
            ))}
          </div>
        ) : properties.length > 0 ? (
          <>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {paginatedProperties.map((property, index) => (
                <PropertyCard key={property.id} property={property} index={index} />
              ))}
            </div>
            <Pagination
              currentPage={safePage}
              totalPages={totalPages}
              onPageChange={handlePageChange}
              testIdPrefix="stays-pagination"
            />
            {totalPages > 1 && (
              <p className="text-center text-sm text-charcoal-muted mt-3" data-testid="stays-page-info">
                Page {safePage} of {totalPages} · {properties.length} total
              </p>
            )}
          </>
        ) : (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="text-center py-16"
          >
            <div className="text-charcoal-muted mb-4">
              <Home className="w-16 h-16 mx-auto opacity-30" />
            </div>
            <h3 className="font-heading text-xl font-medium text-charcoal mb-2">
              No stays found
            </h3>
            <p className="text-charcoal-light mb-6">
              Try adjusting your filters or search criteria
            </p>
            <Button onClick={clearFilters} variant="outline" data-testid="stays-clear-all">
              Clear all filters
            </Button>
          </motion.div>
        )}
      </div>
    </div>
  );
};

export default StaysPage;
